import { FC } from 'react';
import { css } from '@emotion/react';
import { Text } from '@chakra-ui/react';
import { colors, spacing } from '../../../styles';
import { PortfolioKeyEvent } from '../../../apis/backend';

interface EventCardProps {
  event: PortfolioKeyEvent;
  onEventClick: (event: PortfolioKeyEvent) => void;
  onEventKeyDown: (keyboardEvent: React.KeyboardEvent, event: PortfolioKeyEvent) => void;
  maxVisibleSymbols?: number;
}

const EventCard: FC<EventCardProps> = ({ event, onEventClick, onEventKeyDown, maxVisibleSymbols = 3 }) => {
  return (
    <div
      key={event.id}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${event.title}`}
      css={css`
        margin-bottom: ${spacing.deci};
        padding: ${spacing.deci};
        border: 1px solid var(--border-color);
        border-radius: 8px;
        background-color: ${colors.snow};
        cursor: ${event.route && event.route !== '' ? 'pointer' : 'default'};
        transition: background-color 0.2s ease;

        html[data-theme='dark'] & {
          background-color: var(--background-color);
        }

        &:hover {
          background-color: ${colors.cloud};
          html[data-theme='dark'] & {
            background-color: rgba(255, 255, 255, 0.02);
          }
        }

        &:focus {
          outline: 2px solid ${colors.petrolBlue};
          outline-offset: 2px;
          html[data-theme='dark'] & {
            outline-color: ${colors.skyBlue};
          }
        }
      `}
      onClick={() => onEventClick(event)}
      onKeyDown={(e) => onEventKeyDown(e, event)}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: ${spacing.milli};
        `}
      >
        <Text fontWeight="600" fontSize="sm" color="var(--primary-text)">
          {event.title}
        </Text>
        <Text fontSize="xs" color={colors.silver}>
          {new Date(event.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}
        </Text>
      </div>
      <Text fontSize="sm" color="var(--secondary-text)" mb={2}>
        {event.body}
      </Text>
      {event.symbols && event.symbols.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: ${spacing.milli};
          `}
        >
          {event.symbols.slice(0, maxVisibleSymbols).map((symbolBadge) => (
            <span
              key={symbolBadge}
              css={css`
                background-color: ${colors.petrolBlue}15;
                color: ${colors.petrolBlue};
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 11px;
                font-weight: 500;

                html[data-theme='dark'] & {
                  background-color: ${colors.skyBlue}15;
                  color: ${colors.skyBlue};
                }
              `}
            >
              {symbolBadge}
            </span>
          ))}
          {event.symbols.length > maxVisibleSymbols && (
            <span
              css={css`
                color: ${colors.silver};
                font-size: 11px;
              `}
            >
              +{event.symbols.length - maxVisibleSymbols} more
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCard;
